import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Crown, Target } from 'lucide-react'
import { Container } from '../layout/Container'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { useScrollAnimation } from '../../hooks/useScrollAnimation'

export function StartingPoints() {
  const { t } = useTranslation()
  const { ref, isInView } = useScrollAnimation()

  const paths = [
    {
      id: 'clevel',
      icon: Crown,
      title: t('startingPoints.clevel.title'),
      description: t('startingPoints.clevel.description'),
      features: t('startingPoints.clevel.features', { returnObjects: true }) as string[],
      cta: t('startingPoints.clevel.cta'),
    },
    {
      id: 'owner',
      icon: Target,
      title: t('startingPoints.owner.title'),
      description: t('startingPoints.owner.description'),
      features: t('startingPoints.owner.features', { returnObjects: true }) as string[],
      cta: t('startingPoints.owner.cta'),
    },
  ]

  return (
    <section id="comienzo" className="bg-surface py-24">
      <Container size="narrow">
        <div className="mb-16 text-center">
          <span className="text-sm font-medium uppercase tracking-wider text-accent-light">
            {t('startingPoints.label')}
          </span>
          <h2 className="mt-4 text-3xl font-semibold text-text-primary md:text-4xl">
            {t('startingPoints.title')}
          </h2>
        </div>

        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 32 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 32 }}
          transition={{ duration: 0.6 }}
          className="grid gap-6 md:grid-cols-2"
        >
          {paths.map((path) => {
            const Icon = path.icon
            return (
              <Card key={path.id} variant="elevated" className="flex flex-col">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-accent/10 text-accent-light">
                  <Icon className="h-7 w-7" />
                </div>
                <h3 className="mt-6 text-2xl font-semibold text-text-primary">{path.title}</h3>
                <p className="mt-3 text-text-secondary">{path.description}</p>

                <ul className="mt-6 space-y-3">
                  {path.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm text-text-secondary">
                      <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button asChild variant="outline" className="mt-8 w-full">
                  <a href="#diagnostico">{path.cta}</a>
                </Button>
              </Card>
            )
          })}
        </motion.div>
      </Container>
    </section>
  )
}
